import type { AcademicSession, AppState, Enrollment, CoursePeriod, Student } from './types'

export interface EnrollmentRange { from: number; until: number | null }
export interface StudentChange {
  periodId: string
  enrollmentId?: string
  cui?: string
  name?: string
  effectiveWeek?: number
  effectiveSessionNumber?: number
}
export type StudentAction = 'add' | 'withdraw' | 'reactivate' | 'edit'
export const sessionSlot = (s: Pick<AcademicSession, 'week' | 'sessionNumber'>) => (s.week - 1) * 7 + s.sessionNumber
export const eligibleEnrollment = (e: Enrollment, s: AcademicSession) => e.periodId === s.periodId && (e.participation == null
  ? e.active !== false
  : e.participation.some(r => sessionSlot(s) >= r.from && (r.until === null || sessionSlot(s) < r.until)))
export const sessionEnrollments = (app: AppState, s: AcademicSession) => app.enrollments.filter(e => eligibleEnrollment(e, s))
export const enrollmentName = (e: Enrollment, s: Student) => e.name || s.name
export const rosterBoundary = (app: AppState, periodId: string) => Math.max(0, ...app.sessions.filter(s => s.periodId === periodId && (s.status !== 'DRAFT' || s.startedAt || app.attendance.some(a => a.sessionId === s.id))).map(sessionSlot))
export function enrollmentOptions(app: AppState, p: CoursePeriod, withdraw = false) {
  const boundary = rosterBoundary(app, p.id)
  const options = Array.from({ length: p.weeks * p.sessionsPerWeek }, (_, index) => ({ week: Math.floor(index / p.sessionsPerWeek) + 1, sessionNumber: index % p.sessionsPerWeek + 1 }))
  if (withdraw) options.push({ week: p.weeks + 1, sessionNumber: 1 })
  return options.filter(s => sessionSlot(s) > boundary)
}
export const slotLabel = (slot: number) => `W${String(Math.floor((slot - 1) / 7) + 1).padStart(2, '0')}-S${(slot - 1) % 7 + 1}`
export const enrollmentRanges = (e: Enrollment) => e.participation ?? (e.active === false ? [] : [{ from: 1, until: null }])
export function studentError(error: unknown) {
  const messages: Record<string, string> = {
    STUDENT_ALREADY_ENROLLED: 'Este CUI ya tiene matrícula en el periodo. Utiliza reactivar si está retirado.',
    STUDENT_ALREADY_WITHDRAWN: 'El alumno ya está retirado.', STUDENT_ALREADY_ACTIVE: 'El alumno ya tiene una matrícula abierta.',
    ENROLLMENT_HISTORY_LOCKED: 'La modificación no puede afectar sesiones iniciadas o con historial. Elige una sesión posterior.',
    INVALID_EFFECTIVE_SESSION: 'Selecciona una sesión de efecto válida.', CUI_LOCKED: 'El CUI es permanente y no se puede editar.',
    ROSTER_LOCKED: 'Ya existe actividad: usa Agregar alumno con su sesión de ingreso o Corregir nombre.',
    INVALID_STUDENT: 'Introduce un CUI de 1 a 12 dígitos y un nombre de hasta 200 caracteres.',
    DUPLICATE_CUI: 'El archivo contiene CUI duplicados.', FORBIDDEN: 'No tienes permiso para gestionar este alumno.',
    PERIOD_ARCHIVED: 'El periodo archivado es de solo lectura.', STUDENT_DISABLED: 'El alumno tiene una restricción administrativa global.',
  }
  return error instanceof Error ? messages[error.message] || error.message : 'No se pudo completar la operación.'
}
